/*
This Code was mostly generated by AI in the storefronts-prototype repo.
It still needs to be cleaned up and refactored.
*/

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { HoverCard, HoverCardTrigger } from '@/components/ui/hover-card'
import { Input } from '@/components/ui/input'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip'
import { Muted, Small } from '@/components/ui/typography'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { MetaAd } from '@storefronts/common-types'
import { Search, ZoomIn, Upload } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import Link from 'next/link'
import { ManualCreativeUploadModal } from './ManualCreativeUploadModal'
import { AdGridView } from './AdGridView'
import { AdSelectionPaginationFooter } from './AdSelectionPaginationFooter'
import { MetaAdCreativeHoverContent } from './MetaAdCreativeHoverContent'
import { getMetaAdPreviewImageUrl } from './metaAdCreativeUtils'
import { findMetaAdMatchingNameParam } from './metaAdSort'
import { useAdNameParameter } from './useAdNameParameter'
import type { AdSelectionView } from './adSelectionTypes'

export type { AdSelectionView } from './adSelectionTypes'

function parseForNumber(spend: string) {
    // remove all characters that are not digits, periods, or dashes
    return Number(spend.replace(/[^\d.-]/g, ''))
}

export { useAdNameParameter } from './useAdNameParameter'

interface ListViewProps {
    data: MetaAd[]
    selectedId?: MetaAd['id']
    onSelect: (ad: MetaAd) => void
}

// eslint-disable-next-line complexity
export const ListView: React.FC<ListViewProps> = ({
    data,
    selectedId,
    onSelect,
}) => {
    const [currentPage, setCurrentPage] = React.useState(0)
    const adName = useAdNameParameter()

    const itemsPerPage = 100

    // Add sorting state
    const [sortConfig, setSortConfig] = React.useState<{
        key: keyof MetaAd
        direction: 'ascending' | 'descending'
    } | null>({
        key: 'spend',
        direction: 'descending',
    })

    // Sort function for the data
    const sortedData = React.useMemo(() => {
        const sortableData = [...data]
        const preselectedAd = findMetaAdMatchingNameParam(sortableData, adName)
        if (preselectedAd) {
            const pinnedIndex = sortableData.findIndex(
                (ad) => ad.id === preselectedAd.id,
            )
            if (pinnedIndex >= 0) {
                sortableData.splice(pinnedIndex, 1)
            }
        }

        if (sortConfig !== null) {
            sortableData.sort((a, b) => {
                // Handle possible undefined values
                let aValue
                let bValue

                // Define fields that need numeric parsing
                const numericFields = [
                    'spend',
                    'roas',
                    'ctr',
                    'addToCartRate',
                    'checkoutRate',
                    'conversionRate',
                ]

                if (numericFields.includes(sortConfig.key)) {
                    aValue = parseForNumber(a[sortConfig.key] as string)
                    bValue = parseForNumber(b[sortConfig.key] as string)
                } else {
                    aValue = a[sortConfig.key]
                    bValue = b[sortConfig.key]
                }

                // If either value is undefined, handle appropriately
                if (aValue === undefined && bValue === undefined) return 0
                if (aValue === undefined) return 1 // Undefined values go last
                if (bValue === undefined) return -1

                if (aValue === bValue) {
                    return 0
                }

                // If a value is NaN, sort to the bottom
                if (typeof aValue === 'number' && isNaN(aValue)) return 1
                if (typeof bValue === 'number' && isNaN(bValue)) return -1

                if (sortConfig.direction === 'ascending') {
                    return aValue < bValue ? -1 : 1
                } else {
                    return aValue > bValue ? -1 : 1
                }
            })
        }

        if (preselectedAd) {
            sortableData.unshift(preselectedAd)
        }

        return sortableData
    }, [data, sortConfig, adName])

    // Calculate pagination
    const totalPages = Math.ceil(sortedData.length / itemsPerPage)
    const startIndex = currentPage * itemsPerPage
    const paginatedData = sortedData.slice(
        startIndex,
        startIndex + itemsPerPage,
    )

    // Reset page when data changes
    const [prevData, setPrevData] = React.useState(data)
    if (prevData !== data) {
        setPrevData(data)
        setCurrentPage(0)
    }

    // Request sort handler
    const requestSort = (key: keyof MetaAd) => {
        let direction: 'ascending' | 'descending' = 'ascending'

        if (
            sortConfig &&
            sortConfig.key === key &&
            sortConfig.direction === 'ascending'
        ) {
            direction = 'descending'
        } else if (
            sortConfig &&
            sortConfig.key === key &&
            sortConfig.direction === 'descending'
        ) {
            // If already descending, remove sort
            setSortConfig(null)
            return
        }

        setSortConfig({ key, direction })
    }

    return (
        <TooltipProvider>
            <Card className="flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-md border shadow-sm border-border">
                <div className="min-h-0 flex-1 overflow-auto">
                    <RadioGroup
                        value={selectedId ?? ''}
                        onValueChange={(id) => {
                            const ad = paginatedData.find(
                                (item) => item.id === id,
                            )
                            if (ad) onSelect(ad)
                        }}
                        className="contents"
                    >
                        <Table noScrollWrapper>
                            <TableHeader className="sticky top-0 z-10 whitespace-nowrap border-b shadow-[0_1px_0_0_var(--border)] bg-muted border-border">
                                <TableRow className="h-12 border-b border-border">
                                    <TableHead className="w-[70px] text-center">
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    className="px-2 py-3 font-medium text-muted-foreground"
                                                >
                                                    Select
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                Select an ad to continue
                                            </TooltipContent>
                                        </Tooltip>
                                    </TableHead>
                                    <TableHead className="w-[80px] text-center">
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    className="px-2 py-3 font-medium text-muted-foreground"
                                                >
                                                    Creative
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                Ad creative preview
                                            </TooltipContent>
                                        </Tooltip>
                                    </TableHead>
                                    <TableHead className="w-px">
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    className="px-2 py-3 font-medium text-muted-foreground hover:text-info"
                                                    onClick={() =>
                                                        requestSort('name')
                                                    }
                                                >
                                                    Name
                                                    {sortConfig?.key ===
                                                        'name' && (
                                                        <span className="ml-1 text-info">
                                                            {sortConfig.direction ===
                                                            'ascending'
                                                                ? '↑'
                                                                : '↓'}
                                                        </span>
                                                    )}
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                Ad name and campaign (click to
                                                sort)
                                            </TooltipContent>
                                        </Tooltip>
                                    </TableHead>
                                    <TableHead className="w-[90px] text-center">
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    className="w-full px-2 py-3 font-medium text-muted-foreground hover:text-info"
                                                    onClick={() =>
                                                        requestSort('spend')
                                                    }
                                                >
                                                    Spend
                                                    {sortConfig?.key ===
                                                        'spend' && (
                                                        <span className="ml-1">
                                                            {sortConfig.direction ===
                                                            'ascending'
                                                                ? '↑'
                                                                : '↓'}
                                                        </span>
                                                    )}
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                Total ad spend in USD (click to
                                                sort)
                                            </TooltipContent>
                                        </Tooltip>
                                    </TableHead>
                                    <TableHead className="w-[90px] text-center">
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    className="w-full px-2 py-3 font-medium text-muted-foreground hover:text-info"
                                                    onClick={() =>
                                                        requestSort('ctr')
                                                    }
                                                >
                                                    CTR
                                                    {sortConfig?.key ===
                                                        'ctr' && (
                                                        <span className="ml-1">
                                                            {sortConfig.direction ===
                                                            'ascending'
                                                                ? '↑'
                                                                : '↓'}
                                                        </span>
                                                    )}
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                Click-through rate (click to
                                                sort)
                                            </TooltipContent>
                                        </Tooltip>
                                    </TableHead>
                                    <TableHead className="w-[140px] text-center">
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    className="w-full px-2 py-3 font-medium text-muted-foreground hover:text-info"
                                                    onClick={() =>
                                                        requestSort(
                                                            'addToCartRate',
                                                        )
                                                    }
                                                >
                                                    Add to Cart Rate
                                                    {sortConfig?.key ===
                                                        'addToCartRate' && (
                                                        <span className="ml-1">
                                                            {sortConfig.direction ===
                                                            'ascending'
                                                                ? '↑'
                                                                : '↓'}
                                                        </span>
                                                    )}
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                Percentage of clicks that added
                                                to cart (click to sort)
                                            </TooltipContent>
                                        </Tooltip>
                                    </TableHead>
                                    <TableHead className="w-[140px] text-center">
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    className="w-full px-2 py-3 font-medium text-muted-foreground hover:text-info"
                                                    onClick={() =>
                                                        requestSort(
                                                            'checkoutRate',
                                                        )
                                                    }
                                                >
                                                    Checkout Rate
                                                    {sortConfig?.key ===
                                                        'checkoutRate' && (
                                                        <span className="ml-1">
                                                            {sortConfig.direction ===
                                                            'ascending'
                                                                ? '↑'
                                                                : '↓'}
                                                        </span>
                                                    )}
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                Percentage of cart adds that
                                                checked out (click to sort)
                                            </TooltipContent>
                                        </Tooltip>
                                    </TableHead>
                                    <TableHead className="w-[140px] text-center">
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    className="w-full px-2 py-3 font-medium text-muted-foreground hover:text-info"
                                                    onClick={() =>
                                                        requestSort(
                                                            'conversionRate',
                                                        )
                                                    }
                                                >
                                                    Conversion Rate
                                                    {sortConfig?.key ===
                                                        'conversionRate' && (
                                                        <span className="ml-1">
                                                            {sortConfig.direction ===
                                                            'ascending'
                                                                ? '↑'
                                                                : '↓'}
                                                        </span>
                                                    )}
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                Overall conversion rate (click
                                                to sort)
                                            </TooltipContent>
                                        </Tooltip>
                                    </TableHead>
                                    <TableHead className="w-[90px] text-center">
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    className="w-full px-2 py-3 font-medium text-muted-foreground hover:text-info"
                                                    onClick={() =>
                                                        requestSort(
                                                            'totalPurchasesValue',
                                                        )
                                                    }
                                                >
                                                    Revenue
                                                    {sortConfig?.key ===
                                                        'totalPurchasesValue' && (
                                                        <span className="ml-1">
                                                            {sortConfig.direction ===
                                                            'ascending'
                                                                ? '↑'
                                                                : '↓'}
                                                        </span>
                                                    )}
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                Total purchases value (click to
                                                sort)
                                            </TooltipContent>
                                        </Tooltip>
                                    </TableHead>
                                    <TableHead className="w-[90px] text-center">
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    className="w-full px-2 py-3 font-medium text-muted-foreground hover:text-info"
                                                    onClick={() =>
                                                        requestSort('roas')
                                                    }
                                                >
                                                    ROAS
                                                    {sortConfig?.key ===
                                                        'roas' && (
                                                        <span className="ml-1">
                                                            {sortConfig.direction ===
                                                            'ascending'
                                                                ? '↑'
                                                                : '↓'}
                                                        </span>
                                                    )}
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                Return on ad spend (click to
                                                sort)
                                            </TooltipContent>
                                        </Tooltip>
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {paginatedData.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={10}
                                            className="h-24 text-center"
                                        >
                                            <Muted>No ads found</Muted>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedData.map((ad) => (
                                        <TableRow
                                            key={ad.id}
                                            className={`cursor-pointer hover:bg-muted ${selectedId === ad.id ? ' bg-info/10' : ''}`}
                                            onClick={() => onSelect(ad)}
                                        >
                                            <TableCell className="py-2 text-center">
                                                <RadioGroupItem
                                                    value={ad.id}
                                                    aria-label={`Select ${ad.name}`}
                                                    className="mx-auto"
                                                />
                                            </TableCell>
                                            <TableCell className="py-2 text-center">
                                                <HoverCard>
                                                    <HoverCardTrigger asChild>
                                                        <div className="group relative flex justify-center">
                                                            <Avatar className="size-10 border border-border group-hover:brightness-110">
                                                                <AvatarImage
                                                                    src={
                                                                        getMetaAdPreviewImageUrl(
                                                                            ad,
                                                                        ) ||
                                                                        ad.thumbnailUrl ||
                                                                        ad.mediaUrl
                                                                    }
                                                                    alt={
                                                                        ad.name
                                                                    }
                                                                    className="object-cover"
                                                                />
                                                                <AvatarFallback className="bg-muted text-muted-foreground">
                                                                    {ad.name.charAt(
                                                                        0,
                                                                    )}
                                                                </AvatarFallback>
                                                            </Avatar>
                                                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100">
                                                                <div className="absolute inset-0 rounded-sm bg-black/40"></div>
                                                                <ZoomIn className="z-10 size-5 text-white" />
                                                            </div>
                                                        </div>
                                                    </HoverCardTrigger>
                                                    <MetaAdCreativeHoverContent
                                                        ad={ad}
                                                    />
                                                </HoverCard>
                                            </TableCell>
                                            <TableCell className="w-full max-w-0 py-3">
                                                <div className="space-y-1">
                                                    <div className="flex items-center gap-2">
                                                        <span
                                                            className="truncate text-sm font-medium leading-tight text-foreground"
                                                            title={ad.name}
                                                        >
                                                            {ad.name}
                                                        </span>

                                                        <Badge
                                                            variant={
                                                                ad.type ===
                                                                'VIDEO'
                                                                    ? 'default'
                                                                    : 'outline'
                                                            }
                                                            className={`min-h-5 whitespace-nowrap px-2 py-0.5 text-xs`}
                                                        >
                                                            {ad.type ===
                                                            'UNKNOWN'
                                                                ? 'Dynamic Ad'
                                                                : ad.type}
                                                        </Badge>
                                                    </div>

                                                    {ad.campaignName && (
                                                        <div
                                                            className="truncate text-xs leading-tight text-muted-foreground"
                                                            title={
                                                                ad.campaignName
                                                            }
                                                        >
                                                            <span className="font-medium">
                                                                Campaign:
                                                            </span>{' '}
                                                            {ad.campaignName}
                                                        </div>
                                                    )}

                                                    {ad.adSetName && (
                                                        <div
                                                            className="truncate text-xs leading-tight text-muted-foreground"
                                                            title={ad.adSetName}
                                                        >
                                                            <span className="font-medium">
                                                                Ad Set:
                                                            </span>{' '}
                                                            {ad.adSetName}
                                                        </div>
                                                    )}

                                                    {ad.creativeLinkUrl && (
                                                        <div className="truncate text-xs leading-tight text-muted-foreground">
                                                            <span className="font-medium">
                                                                Destination:
                                                            </span>{' '}
                                                            <Link
                                                                href={
                                                                    ad.creativeLinkUrl
                                                                }
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                {
                                                                    ad.creativeLinkUrl
                                                                }
                                                            </Link>
                                                        </div>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className="py-2 text-center">
                                                <Small className="font-medium text-muted-foreground">
                                                    {ad.spend}
                                                </Small>
                                            </TableCell>
                                            <TableCell className="py-2 text-center">
                                                <Small>{ad.ctr}</Small>
                                            </TableCell>
                                            <TableCell className="py-2 text-center">
                                                <Small>
                                                    {ad.addToCartRate}
                                                </Small>
                                            </TableCell>
                                            <TableCell className="py-2 text-center">
                                                <Small>{ad.checkoutRate}</Small>
                                            </TableCell>
                                            <TableCell className="py-2 text-center">
                                                <Small>
                                                    {ad.conversionRate}
                                                </Small>
                                            </TableCell>
                                            <TableCell className="py-2 text-center">
                                                <Small>
                                                    {ad.totalPurchasesValue}
                                                </Small>
                                            </TableCell>
                                            <TableCell className="py-2 text-center">
                                                <Small>{ad.roas}</Small>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </RadioGroup>
                </div>
                {totalPages > 1 && (
                    <AdSelectionPaginationFooter
                        startIndex={startIndex}
                        endIndex={Math.min(
                            startIndex + itemsPerPage,
                            sortedData.length,
                        )}
                        totalCount={sortedData.length}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPrevious={() =>
                            setCurrentPage((prev) => Math.max(0, prev - 1))
                        }
                        onNext={() =>
                            setCurrentPage((prev) =>
                                Math.min(totalPages - 1, prev + 1),
                            )
                        }
                    />
                )}
            </Card>
        </TooltipProvider>
    )
}

interface AdSelectionProps {
    ads: MetaAd[]
    selectedAdId?: MetaAd['id']
    onAdSelect: (ad: MetaAd) => void
    onContinue?: () => void
    onManualUpload?: (sessionId: string) => Promise<void>
    boundedHeight?: boolean
    defaultView?: AdSelectionView
    view?: AdSelectionView
    onViewChange?: (view: AdSelectionView) => void
}

const AdSelection: React.FC<AdSelectionProps> = ({
    ads,
    selectedAdId,
    onAdSelect,
    onManualUpload,
    boundedHeight,
    defaultView = 'list',
    view: viewProp,
}) => {
    const [uncontrolledView] = useState<AdSelectionView>(defaultView)
    const view = viewProp ?? uncontrolledView
    const [searchQuery, setSearchQuery] = useState('')
    const [isNavigating] = useState(false)
    const [isManualUploadModalOpen, setIsManualUploadModalOpen] =
        useState(false)

    // Filter states
    const [accountFilter, setAccountFilter] = useState<string>('all')
    const [campaignFilter, setCampaignFilter] = useState<string>('all')
    const [adSetFilter, setAdSetFilter] = useState<string>('all')
    const [statusFilter, setStatusFilter] = useState<string>('all')
    const [typeFilter, setTypeFilter] = useState<string>('image_video')

    const accounts = useMemo(() => {
        return Array.from(
            new Set(ads.map((ad) => ad.accountName || '').filter(Boolean)),
        ).sort()
    }, [ads])

    const handleAccountChange = (value: string) => {
        setAccountFilter(value)
        setCampaignFilter('all')
        setAdSetFilter('all')
    }

    const handleCampaignChange = (value: string) => {
        setCampaignFilter(value)
        setAdSetFilter('all')
    }

    // Get unique campaigns, adsets and generate options
    const campaigns = useMemo(() => {
        const scopedAds =
            accountFilter === 'all'
                ? ads
                : ads.filter((ad) => ad.accountName === accountFilter)
        return Array.from(
            new Set(
                scopedAds.map((ad) => ad.campaignName || '').filter(Boolean),
            ),
        ).sort()
    }, [ads, accountFilter])

    const adSets = useMemo(() => {
        const scopedAds = ads.filter(
            (ad) =>
                (accountFilter === 'all' || ad.accountName === accountFilter) &&
                (campaignFilter === 'all' ||
                    ad.campaignName === campaignFilter),
        )
        return Array.from(
            new Set(scopedAds.map((ad) => ad.adSetName || '').filter(Boolean)),
        ).sort()
    }, [ads, accountFilter, campaignFilter])

    // Filter data based on search query and filters
    const filteredData = useMemo(() => {
        return ads.filter((ad) => {
            // Search query filter
            const query = searchQuery.toLowerCase()
            const matchesSearch =
                !searchQuery ||
                ad.name.toLowerCase().includes(query) ||
                (ad.creativeLinkUrl?.toLowerCase().includes(query) ?? false)

            // Account filter
            const matchesAccount =
                accountFilter === 'all' || ad.accountName === accountFilter

            // Campaign filter
            const matchesCampaign =
                campaignFilter === 'all' || ad.campaignName === campaignFilter

            // Ad set filter
            const matchesAdSet =
                adSetFilter === 'all' || ad.adSetName === adSetFilter

            // Status filter
            const matchesStatus =
                statusFilter === 'all' || ad.status === statusFilter

            // Type filter
            let matchesType = false
            if (typeFilter === 'all') matchesType = true
            else if (typeFilter === 'image_video')
                matchesType = ad.type === 'IMAGE' || ad.type === 'VIDEO'
            else matchesType = ad.type === typeFilter

            return (
                matchesSearch &&
                matchesAccount &&
                matchesCampaign &&
                matchesAdSet &&
                matchesStatus &&
                matchesType
            )
        })
    }, [
        ads,
        searchQuery,
        accountFilter,
        campaignFilter,
        adSetFilter,
        statusFilter,
        typeFilter,
    ])

    // If navigating away, render nothing
    if (isNavigating) return null

    return (
        <div
            className={cn(
                'relative flex size-full flex-col space-y-3 px-2',
                boundedHeight && 'min-h-0 overflow-hidden',
            )}
        >
            <div className="flex shrink-0 flex-col gap-2">
                <div className="grid grid-cols-6 gap-2">
                    <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
                        <Input
                            placeholder="Search ads..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="h-8 pl-8"
                        />
                    </div>
                    <Select
                        value={accountFilter}
                        onValueChange={handleAccountChange}
                    >
                        <SelectTrigger className="h-8">
                            <SelectValue placeholder="Account" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Accounts</SelectItem>
                            {accounts.map((account) => (
                                <SelectItem key={account} value={account}>
                                    {account}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Select
                        value={campaignFilter}
                        onValueChange={handleCampaignChange}
                    >
                        <SelectTrigger className="h-8">
                            <SelectValue placeholder="Campaign" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Campaigns</SelectItem>
                            {campaigns.map((campaign) => (
                                <SelectItem key={campaign} value={campaign}>
                                    {campaign}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Select value={adSetFilter} onValueChange={setAdSetFilter}>
                        <SelectTrigger className="h-8">
                            <SelectValue placeholder="Ad Set" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Ad Sets</SelectItem>
                            {adSets.map((adSet) => (
                                <SelectItem key={adSet} value={adSet}>
                                    {adSet}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Select
                        value={statusFilter}
                        onValueChange={setStatusFilter}
                    >
                        <SelectTrigger className="h-8">
                            <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Statuses</SelectItem>
                            <SelectItem value="ACTIVE">Active</SelectItem>
                            <SelectItem value="PAUSED">Paused</SelectItem>
                        </SelectContent>
                    </Select>

                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                        <SelectTrigger className="h-8">
                            <SelectValue placeholder="Type" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Types</SelectItem>
                            <SelectItem value="image_video">
                                Image & Video
                            </SelectItem>
                            <SelectItem value="IMAGE">Image Only</SelectItem>
                            <SelectItem value="VIDEO">Video Only</SelectItem>
                            <SelectItem value="UNKNOWN">Dynamic Ads</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {onManualUpload && (
                    <div className="flex justify-end">
                        <Button
                            variant="outline"
                            onClick={() => setIsManualUploadModalOpen(true)}
                            className="h-8"
                        >
                            <Upload className="mr-2 size-4" />
                            Upload
                        </Button>
                    </div>
                )}
            </div>

            {boundedHeight ? (
                <div className="flex min-h-0 flex-1 flex-col">
                    {view === 'grid' ? (
                        <AdGridView
                            data={filteredData}
                            selectedId={selectedAdId}
                            onSelect={onAdSelect}
                        />
                    ) : (
                        <ListView
                            data={filteredData}
                            selectedId={selectedAdId}
                            onSelect={onAdSelect}
                        />
                    )}
                </div>
            ) : view === 'grid' ? (
                <AdGridView
                    data={filteredData}
                    selectedId={selectedAdId}
                    onSelect={onAdSelect}
                />
            ) : (
                <ListView
                    data={filteredData}
                    selectedId={selectedAdId}
                    onSelect={onAdSelect}
                />
            )}

            {/* Manual Upload Modal */}
            {onManualUpload && (
                <ManualCreativeUploadModal
                    open={isManualUploadModalOpen}
                    onOpenChange={setIsManualUploadModalOpen}
                    onSubmit={async (sessionId) => {
                        await onManualUpload(sessionId)
                        setIsManualUploadModalOpen(false)
                    }}
                />
            )}
        </div>
    )
}

export default AdSelection
